import { browser } from "$app/environment";
import { createWarmHallIR } from "./reverb";
import { unlockAudioContext } from "./unlock";

/**
 * VuAudio — singleton Web Audio engine for the VU ecosystem.
 *
 * Zero external assets. All sounds are synthesized at runtime from raw
 * oscillators and noise. Sound modules ask the engine for a (dry, wet)
 * bus pair and connect their oscillators into both — the engine handles
 * reverb, compression, master volume, and mute.
 *
 * Audio graph:
 *   sources  ─┬──▶ dryGain ──┐
 *             │              ├──▶ compressor ──▶ masterGain ──▶ destination
 *             └──▶ wetGain ──▶ reverb (convolver) ──┘
 *
 * Reactivity: Svelte 5 runes (`$state`) so any component can bind to
 * `vuAudio.muted` / `vuAudio.volume` and re-render on change.
 */
class VuAudioEngine {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private reverbNode: ConvolverNode | null = null;
  private dryGain: GainNode | null = null;
  private wetGain: GainNode | null = null;
  private compressor: DynamicsCompressorNode | null = null;

  /** True once `init()` has finished and the context is "running". */
  ready = $state(false);
  muted = $state(false);
  /** Linear master gain, 0.0 – 1.0. */
  volume = $state(0.7);

  /**
   * Initialise the engine. Must be called from inside a user-gesture
   * handler (click, touchstart, keydown, pointerdown) on iOS / Safari,
   * otherwise the resulting context will stay suspended forever.
   *
   * Idempotent — subsequent calls are no-ops.
   */
  async init(): Promise<void> {
    if (!browser) return;
    if (this.ctx) return;

    const AC =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!AC) {
      console.warn("[VuAudio] Web Audio API not supported");
      return;
    }

    this.ctx = new AC();

    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = this.muted ? 0 : this.volume;

    this.compressor = this.ctx.createDynamicsCompressor();
    this.compressor.threshold.value = -18;
    this.compressor.knee.value = 12;
    this.compressor.ratio.value = 3;
    this.compressor.attack.value = 0.005;
    this.compressor.release.value = 0.12;

    this.reverbNode = this.ctx.createConvolver();
    this.reverbNode.buffer = createWarmHallIR(this.ctx);

    this.dryGain = this.ctx.createGain();
    this.dryGain.gain.value = 0.75;

    this.wetGain = this.ctx.createGain();
    this.wetGain.gain.value = 0.3;

    this.dryGain.connect(this.compressor);
    this.wetGain.connect(this.reverbNode).connect(this.compressor);
    this.compressor.connect(this.masterGain);
    this.masterGain.connect(this.ctx.destination);

    await unlockAudioContext(this.ctx);
    this.ready = true;
  }

  /**
   * Returns the (dry, wet) bus pair sound modules connect into. Returns
   * null if the engine isn't initialised yet — callers should fall back
   * to a no-op rather than crashing the UI.
   */
  getBus(): { ctx: AudioContext; dry: GainNode; wet: GainNode } | null {
    if (!this.ctx || !this.dryGain || !this.wetGain) return null;
    return { ctx: this.ctx, dry: this.dryGain, wet: this.wetGain };
  }

  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
    if (this.masterGain && this.ctx) {
      this.masterGain.gain.linearRampToValueAtTime(
        this.muted ? 0 : this.volume,
        this.ctx.currentTime + 0.05,
      );
    }
  }

  toggleMute(): void {
    this.muted = !this.muted;
    if (this.masterGain && this.ctx) {
      this.masterGain.gain.linearRampToValueAtTime(
        this.muted ? 0 : this.volume,
        this.ctx.currentTime + 0.05,
      );
    }
  }

  /**
   * Apply persisted preferences from `loadPrefs()`. Safe to call before
   * `init()` — it just updates the rune state, which the engine reads
   * when it subsequently builds the master gain node.
   */
  applyPrefs(prefs: { muted?: boolean; volume?: number }): void {
    if (typeof prefs.muted === "boolean") this.muted = prefs.muted;
    if (typeof prefs.volume === "number") {
      this.volume = Math.max(0, Math.min(1, prefs.volume));
    }
    if (this.masterGain && this.ctx) {
      this.masterGain.gain.linearRampToValueAtTime(
        this.muted ? 0 : this.volume,
        this.ctx.currentTime + 0.05,
      );
    }
  }

  /** Honor the OS's reduced-motion preference. */
  get prefersReducedMotion(): boolean {
    if (!browser) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  /** Suspend the context to save battery while idle. */
  async suspend(): Promise<void> {
    if (this.ctx && this.ctx.state === "running") {
      await this.ctx.suspend();
    }
  }

  async resume(): Promise<void> {
    if (this.ctx && this.ctx.state === "suspended") {
      await this.ctx.resume();
    }
  }
}

export const vuAudio = new VuAudioEngine();
